import type { TFunction } from "i18next"

import { PaymentMethodNameEnum } from "@/enums"
import type { SubscriptionStatusEnum } from "@/enums/subscriptions"
import type {
  PaymentMethod,
  Subscription,
} from "@/types/subscriptions"

const ACTIVE_PAYMENT_METHOD_STATUS = "ACTIVE"
import {
  CycleStatusColorsEnum,
  CycleStatusEnum,
  cycleStatusLabels,
  SubscriptionStatusColorsEnum,
  subscriptionStatusLabels,
} from "@/enums/subscriptions"

export const CANCEL_PAYMENT_METHODS = [
  PaymentMethodNameEnum.PIX,
  PaymentMethodNameEnum.BANK_SLIP,
  PaymentMethodNameEnum.DEBIT_CARD,
  PaymentMethodNameEnum.DIRECT_DEBIT,
  PaymentMethodNameEnum.BOLEPIX,
] as const

export const isCancelPaymentMethodByType = (
  paymentMethod?: PaymentMethodNameEnum
) =>
  CANCEL_PAYMENT_METHODS.includes(
    paymentMethod as (typeof CANCEL_PAYMENT_METHODS)[number]
  )

export const SUBSCRIPTION_TYPE_VTEX = "VTEX"

// Reverter é desfazer a cobrança no banco — só os meios que têm baixa. Cartão sai por estorno,
// que já é a ação "Reembolsar" no mesmo menu.
export const REVERSIBLE_CYCLE_METHODS: PaymentMethodNameEnum[] =
  [
    PaymentMethodNameEnum.BANK_SLIP,
    PaymentMethodNameEnum.BOLEPIX,
    PaymentMethodNameEnum.PIX,
  ]

// A regra de entrada é do BACKEND, e ela olha o PEDIDO, não o ciclo:
// `Order.isAbleToReversal()` = `orderStatus.isAbleToCancel() && paymentId != null`, e
// `cancellableStatus` inclui os sete `WAITING_*`. Ou seja, o ciclo entra no fluxo SEM estar pago —
// no boleto e no bolepix é exatamente esse o caso de uso: baixar o título antes que alguém pague.
//
// Daí a lista ser de EXCLUSÃO, e curta. `PROCESSING_PAYMENT` é o estado normal do ciclo de boleto
// aguardando pagamento (o status é gravado antes de o título ser gerado, e o ciclo não tem um
// "aguardando" próprio) — bloqueá-lo, como a lista do estorno faz, recusa justamente o caso
// principal.
const NON_REVERSIBLE_CYCLE_STATUSES: string[] = [
  // Já desfeito — no modo forçado seria registrado de novo.
  CycleStatusEnum.REVERSED,
  CycleStatusEnum.REFUNDED,
  CycleStatusEnum.PARTIAL_REFUNDED,
  CycleStatusEnum.CANCELED,
  CycleStatusEnum.CANCELED_BY_DELEGATE_UPDATE,
  CycleStatusEnum.CANCELED_BY_AMOUNT_UPDATE,
  // Desfazer em andamento — disparar de novo duplica a chamada ao processador.
  CycleStatusEnum.PROCESSING_REVERSING,
  CycleStatusEnum.PROCESSING_REFUNDING,
]

export interface ReversibleCycle {
  id?: string
  subscriptionId?: string
  orderSubscriptionId?: string
  subscriptionType?: string
  paymentId?: string
  status?: string
  paymentMethod?: PaymentMethodNameEnum
}

/**
 * Se o ciclo aceita reversão — a decisão vive aqui porque o ciclo é desenhado em DOIS lugares
 * (a tabela em cards e a tabela em colunas), e a regra divergir entre eles significa a ação
 * aparecer num e sumir no outro.
 */
export const canReverseCycle = (
  cycle: ReversibleCycle,
  canWrite: boolean
) =>
  Boolean(
    canWrite &&
    cycle.paymentId &&
    cycle.id &&
    (cycle.subscriptionId || cycle.orderSubscriptionId) &&
    cycle.subscriptionType !== SUBSCRIPTION_TYPE_VTEX &&
    REVERSIBLE_CYCLE_METHODS.includes(
      cycle.paymentMethod as PaymentMethodNameEnum
    ) &&
    !NON_REVERSIBLE_CYCLE_STATUSES.includes(
      cycle.status as string
    )
  )

export const getCycleStatusInfo = (
  status: CycleStatusEnum,
  t: TFunction,
  paymentMethod?: PaymentMethodNameEnum
) => {
  const isCancelPaymentMethod =
    isCancelPaymentMethodByType(paymentMethod)

  if (isCancelPaymentMethod) {
    if (status === CycleStatusEnum.REVERSED) {
      return {
        label: t("subscriptions.cycleStatus.canceled"),
        bgColor: CycleStatusColorsEnum.CANCELED,
      }
    }

    if (status === CycleStatusEnum.PROCESSING_REVERSING) {
      return {
        label: t(
          "subscriptions.cycleStatus.processingCanceling"
        ),
        bgColor: CycleStatusColorsEnum.CANCELED,
      }
    }
  }

  return {
    label: t(cycleStatusLabels[status]),
    bgColor: CycleStatusColorsEnum[status],
  }
}

export const getSubscriptionMainStatusInfo = (
  status: SubscriptionStatusEnum,
  t: TFunction
) => {
  return {
    label: t(subscriptionStatusLabels[status]),
    bgColor: SubscriptionStatusColorsEnum[status],
  }
}

export const getInsuranceTypeLabel = (
  type: string,
  t: TFunction
) => {
  return t(`insuranceType.${type}`)
}

type PaymentMethodColumnSource = Partial<
  Pick<Subscription, "paymentMethodsListView">
> & {
  paymentMethods?:
    Pick<PaymentMethod, "type" | "status">[] | null
}

/**
 * Texto da coluna "Meio de Pagamento" da listagem de assinaturas.
 *
 * Uma assinatura pode ter mais de um meio ativo (ex.: Cartão e PIX). Exibir só o primeiro
 * fazia a linha contradizer o filtro — filtrando por PIX, aparecia "Cartão de Crédito".
 *
 * @param subscription - assinatura com `paymentMethods` e, opcionalmente, `paymentMethodsListView`
 * @param getLabel - rótulo exibível de um tipo de meio de pagamento (catálogo da marca / i18n)
 * @param filteredPaymentMethod - tipo do filtro ativo na listagem, se houver
 * @returns o texto a exibir na coluna
 */
export const getPaymentMethodColumnLabel = (
  subscription: PaymentMethodColumnSource,
  getLabel: (type: string) => string,
  filteredPaymentMethod?: string
): string => {
  const activeTypes = (subscription.paymentMethods ?? [])
    .filter(
      (pm) => pm.status === ACTIVE_PAYMENT_METHOD_STATUS
    )
    .map((pm) => pm.type)

  if (activeTypes.length > 0) {
    const orderedTypes = [...activeTypes].sort(
      (a, b) =>
        Number(b === filteredPaymentMethod) -
        Number(a === filteredPaymentMethod)
    )
    return [...new Set(orderedTypes)]
      .map(getLabel)
      .join(", ")
  }

  return subscription.paymentMethodsListView?.trim() || "-"
}
